import { useEffect, useMemo, useState } from 'react';
import { Activity, ArrowRight, BarChart3, CheckCircle2, Clock3, Database, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MODULES } from '../config/modules';
import { useAuth } from '../context/AuthContext';
import { subscribeToCollection } from '../services/data';
import { fetchCurrentlyActiveCount } from '../services/analytics';
import { formatTimestamp, Notice, PageLoader } from '../components/UI';

export default function DashboardPage() {
  const { adminProfile, isSuperAdmin, can } = useAuth();
  const [collections, setCollections] = useState({});
  const [liveActive, setLiveActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCurrentlyActiveCount().then((res) => setLiveActive(res.activeCount)).catch(() => {});
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const available = Object.entries(MODULES).filter(([key]) => can(key, 'view'));
    if (!available.length) {
      setLoading(false);
      return undefined;
    }
    let settled = 0;
    const unsubs = available.map(([key, module]) => subscribeToCollection(module.collection, (rows) => {
      setCollections((current) => ({ ...current, [key]: rows }));
      settled += 1;
      if (settled >= available.length) setLoading(false);
    }, (snapshotError) => {
      setError(snapshotError.message || 'Dashboard data load failed.');
      setLoading(false);
    }));
    return () => unsubs.forEach((unsubscribe) => unsubscribe());
  }, [can]);

  const totals = useMemo(() => Object.values(collections).reduce((result, rows) => ({
    total: result.total + rows.length,
    active: result.active + rows.filter((row) => row.isActive !== false).length,
  }), { total: 0, active: 0 }), [collections]);

  const recentItems = useMemo(() => Object.entries(collections).flatMap(([key, rows]) => rows.map((row) => ({ ...row, moduleKey: key })))
    .filter((item) => item.updatedAt)
    .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0))
    .slice(0, 5), [collections]);

  return (
    <div className="page-stack">
      <section className="welcome-banner">
        <div><p className="eyebrow light">Portal overview</p><h1>Namaste, {adminProfile?.name?.split(' ')[0] || 'Admin'} 👋</h1><p>Jashapar app nu live content ahi thi manage ane publish kari shako cho.</p></div>
        <span className="welcome-art"><Sparkles size={31} /></span>
      </section>

      {isSuperAdmin ? (
        <Link
          to="/analytics"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
            padding: '14px 20px',
            background: 'linear-gradient(90deg, #ecfdf5, #f0fdf4)',
            border: '1px solid #a7f3d0',
            borderRadius: '14px',
            textDecoration: 'none',
            boxShadow: '0 2px 10px rgba(16, 185, 129, 0.08)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', color: '#065f46', fontWeight: 700, fontSize: '14px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981', boxShadow: '0 0 8px #34d399' }} />
            <BarChart3 size={18} color="#059669" />
            <span><strong>App Analytics Live:</strong> {liveActive} users currently active right now</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#047857', fontWeight: 700, fontSize: '13px' }}>
            View Full Analytics & Top Rankings <ArrowRight size={16} />
          </span>
        </Link>
      ) : null}

      {error ? <Notice type="error">{error}</Notice> : null}
      {loading ? <PageLoader label="Dashboard data loading…" /> : (
        <>
          <section className="stat-grid">
            <article className="stat-card"><span className="stat-icon green"><Database /></span><div><small>Total records</small><strong>{totals.total}</strong><p>Accessible collections</p></div></article>
            <article className="stat-card"><span className="stat-icon blue"><CheckCircle2 /></span><div><small>Published</small><strong>{totals.active}</strong><p>Visible in mobile app</p></div></article>
            <article className="stat-card"><span className="stat-icon amber"><Clock3 /></span><div><small>Inactive drafts</small><strong>{totals.total - totals.active}</strong><p>Hidden from public app</p></div></article>
            <article className="stat-card"><span className="stat-icon purple"><Activity /></span><div><small>Modules</small><strong>{Object.keys(collections).length}</strong><p>Your permitted sections</p></div></article>
          </section>

          <section className="dashboard-grid">
            <article className="panel-card module-overview">
              <header className="panel-header"><div><h2>Content modules</h2><p>Live collection status</p></div></header>
              <div className="module-list">
                {Object.entries(MODULES).filter(([key]) => can(key, 'view')).map(([key, module]) => {
                  const rows = collections[key] || [];
                  return (
                    <Link to={`/content/${key}`} className="module-row" key={key}>
                      <span className="module-icon" style={{ color: module.color, backgroundColor: `${module.color}14` }}><module.icon size={19} /></span>
                      <div><strong>{module.label}</strong><span>{rows.filter((row) => row.isActive !== false).length} active of {rows.length}</span></div>
                      <ArrowRight size={17} />
                    </Link>
                  );
                })}
              </div>
            </article>

            <article className="panel-card recent-panel">
              <header className="panel-header"><div><h2>Recent updates</h2><p>Latest Firestore changes</p></div></header>
              <div className="recent-list">
                {recentItems.length ? recentItems.map((item) => {
                  const module = MODULES[item.moduleKey];
                  return (
                    <div className="recent-row" key={`${item.moduleKey}-${item.id}`}>
                      <span className="recent-dot" style={{ backgroundColor: module.color }} />
                      <div><strong>{item.name?.en || item.from?.en || item.id}</strong><span>{module.singular} · {formatTimestamp(item.updatedAt)}</span></div>
                    </div>
                  );
                }) : <p className="muted-block">Updates haji available nathi.</p>}
              </div>
            </article>
          </section>
        </>
      )}
    </div>
  );
}
